import { useQuery } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import {
  CircularProgress,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { DOCUMENTS_TYPES, INVOICE_ATTACHABLE_DOCUMENTS } from '../../graphql';
import { InvoiceUploadedDocumentsTable } from '../attach';
import { FingoDialog } from '../dialogs';
import { documentToAttachableList } from '../../helpers/document-to-attachable-list';
import {
  checkAlreadyUploaded,
  checkXmlErrors,
  emptyDocument,
} from '../../helpers/invoice-document-upload';
import useGetInvoice from '../../hooks/useGetInvoice';
import useInvoiceDocumentUpload from '../../hooks/useInvoiceDocumentUpload';
import useIsStaff from '../../hooks/useIsStaff';
import DocumentFilePicker from './DocumentFilePicker';
import { DocumentTypeSelect } from '../selects';

const UploadDocumentsManager = ({
  open,
  handleClose,
  invoiceId,
}) => {
  const [document, setDocument] = useState({ ...emptyDocument });
  const { name, documentType, file } = document;
  const rowError = checkXmlErrors(documentType, name);
  const isUploadDisabled = name === '' || documentType === '';

  const canReplaceProviderDocuments = useIsStaff();
  const { data: documentsData } = useQuery(DOCUMENTS_TYPES, { skip: !open });
  const invoice = useGetInvoice(open && invoiceId, INVOICE_ATTACHABLE_DOCUMENTS);
  const hasInvoiceProvider = invoice?.company?.hasInvoiceProviderCredentials;
  const attachableList = invoice ? documentToAttachableList(invoice) : [];
  const isInvoiceReady = invoice?.id === invoiceId;

  const handleUploadSuccess = useCallback(() => {
    setDocument({ ...emptyDocument });
  }, []);

  const { handleUpload, loading: loadingUploadFiles } = useInvoiceDocumentUpload({
    invoiceId,
    showSnackbar: true,
    onSuccess: handleUploadSuccess,
  });

  useEffect(() => {
    setDocument({ ...emptyDocument });
  }, [invoiceId, open]);

  const handleUploadClick = useCallback(() => {
    if (!file || !documentType) return;
    handleUpload(file, documentType);
  }, [documentType, file, handleUpload]);

  const setDocumentType = useCallback((value) => {
    setDocument((previous) => ({
      ...previous,
      documentType: value,
    }));
  }, []);

  const handleClearFile = useCallback(() => {
    setDocument((previous) => ({
      ...previous,
      name: '',
      file: '',
    }));
  }, []);

  const handleAddDocument = useCallback((selectedFile) => {
    setDocument((previous) => ({
      ...previous,
      name: selectedFile.name,
      file: selectedFile,
    }));
  }, []);

  const isAlreadyUploaded = useCallback(
    (method) => checkAlreadyUploaded(method, { hasInvoiceProvider, attachableList }),
    [hasInvoiceProvider, attachableList],
  );

  return (
    <FingoDialog
      open={open}
      handleClose={handleClose}
      title="Revisión de documentos"
      PaperProps={{
        sx: {
          borderRadius: 4,
          width: 770,
          maxWidth: 770,
        },
      }}
    >
      <Stack gap="25px" direction="column">
        <Typography variant="small_3" textAlign="center" fontWeight={500}>
          Cargando documentos a Factura{' '}
          <Typography component="span" variant="small_3" fontWeight={600}>
            {invoice?.folio}
          </Typography>
          {' '}de{' '}
          <Typography component="span" variant="small_3" fontWeight={600}>
            {invoice?.company?.name}
          </Typography>
        </Typography>
        <Stack
          spacing={1}
          alignItems={isInvoiceReady ? 'stretch' : 'center'}
          sx={{ backgroundColor: 'gray.A100', p: '8px 24px', borderRadius: '8px' }}
        >
          {!isInvoiceReady ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Stack
                direction="row"
                alignItems="stretch"
                spacing={1}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="stretch">
                  <DocumentFilePicker
                    inputId={`upload-document-file-${invoiceId}`}
                    fileName={name}
                    onFileChange={handleAddDocument}
                    onClear={handleClearFile}
                    sx={{ width: 220, maxWidth: 220 }}
                  />
                  <DocumentTypeSelect
                    value={documentType}
                    onChange={setDocumentType}
                    options={documentsData?.documentsTypes ?? []}
                    isAlreadyUploaded={isAlreadyUploaded}
                    allowAlreadyUploaded={canReplaceProviderDocuments}
                    sx={{ width: 220, maxWidth: 220 }}
                  />
                </Stack>
                <LoadingButton
                  variant="contained"
                  onClick={handleUploadClick}
                  color="primary"
                  autoFocus
                  size="small"
                  disabled={isUploadDisabled}
                  loading={loadingUploadFiles}
                  id="uploadDocumentUpload"
                  sx={{ height: 48 }}
                >
                  Cargar
                </LoadingButton>
              </Stack>
              {Boolean(rowError) && <FormHelperText error>{rowError}</FormHelperText>}
            </>
          )}
        </Stack>
        <InvoiceUploadedDocumentsTable key={invoiceId} invoiceId={invoiceId} />
      </Stack>
    </FingoDialog>
  );
};

UploadDocumentsManager.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  invoiceId: PropTypes.string.isRequired,
};

export default UploadDocumentsManager;
